import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isSuspiciousSubmission, addTrackingToForm } from '../../utils/tracking';
import { AISearchOptimization } from '../../components/feature/AISearchOptimization';
import { Footer } from '../../components/feature/Footer';

export default function PricingPage() {
  const [showPreviewForm, setShowPreviewForm] = useState(false);
  const [showAITooltip, setShowAITooltip] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    customerName: '',
    businessName: '',
    email: '',
    phone: '',
    serviceType: '',
    honeypot: '', // Honeypot field
    mathAnswer: '' // Math verification
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [formErrors, setFormErrors] = useState({
    email: ''
  });

  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Pricing | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Transparent web design pricing. Starter $499, Professional $999, Enterprise $1,999. No hidden fees. You own 100% of your website.');
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Transparent Flat-Rate Website Packages | Starting at $399 | No Hidden Fees, Pay After Approval | BWP');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'View our simple, flat-rate packages starting at just $399. Get a professional website with guaranteed pricing and our exclusive Pay After Approval promise.');
    }

    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', 'Transparent Flat-Rate Website Packages | Starting at $399 | No Hidden Fees, Pay After Approval | BWP');
    }

    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', 'View our simple, flat-rate packages starting at just $399. Get a professional website with guaranteed pricing and our exclusive Pay After Approval promise.');
    }

    // Generate initial math question
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathQuestion({ num1, num2, answer: num1 + num2 });
  }, []);

  // Fake/disposable email domains to block
  const blockedDomains = [
    'test.com', 'fake.com', 'example.com', 'temp-mail.com', 'tempmail.com',
    'throwaway.email', '10minutemail.com', 'guerrillamail.com', 'mailinator.com',
    'trashmail.com', 'yopmail.com', 'fakeinbox.com', 'maildrop.cc'
  ];

  // Stricter email validation
  const validateEmail = (email: string): { valid: boolean; error: string } => {
    // Basic format check
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }

    // Extract domain
    const domain = email.split('@')[1].toLowerCase();

    // Check against blocked domains
    if (blockedDomains.includes(domain)) {
      return { valid: false, error: 'Please use a valid business or personal email address' };
    }

    // Check for common typos
    const commonTypos: Record<string, string> = {
      'gmial.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'yahooo.com': 'yahoo.com',
      'yaho.com': 'yahoo.com',
      'hotmial.com': 'hotmail.com',
      'outlok.com': 'outlook.com'
    };

    if (commonTypos[domain]) {
      return { valid: false, error: `Did you mean ${email.split('@')[0]}@${commonTypos[domain]}?` };
    }

    return { valid: true, error: '' };
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData({ ...formData, email });

    // Validate email on change
    if (email) {
      const validation = validateEmail(email);
      setFormErrors({ ...formErrors, email: validation.error });
    } else {
      setFormErrors({ ...formErrors, email: '' });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Bot protection checks
    // 1. Honeypot check - if filled, it's a bot
    if (formData.honeypot) {
      console.log('Bot detected via honeypot');
      return;
    }

    // 2. Math verification check
    if (parseInt(formData.mathAnswer) !== mathQuestion.answer) {
      alert('Incorrect answer to the math question. Please try again.');
      return;
    }

    // 3. Time-based bot detection - Block submissions under 60 seconds
    const suspicionCheck = isSuspiciousSubmission();
    if (suspicionCheck.suspicious) {
      alert(suspicionCheck.reason);
      return;
    }

    // Validate email before submission
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      setFormErrors({ ...formErrors, email: emailValidation.error });
      return;
    }

    const formElement = e.target as HTMLFormElement;
    
    // Add tracking data to form
    await addTrackingToForm(formElement);
    
    const formDataToSend = new FormData(formElement);
    
    // Remove honeypot and math answer from submission
    formDataToSend.delete('honeypot');
    formDataToSend.delete('mathAnswer');
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4qdq67iu80n4l8qo160', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => {
          setShowPreviewForm(false);
          setFormSubmitted(false);
          setFormData({
            customerName: '',
            businessName: '',
            email: '',
            phone: '',
            serviceType: '',
            honeypot: '',
            mathAnswer: ''
          });
          
          // Generate new math question
          const num1 = Math.floor(Math.random() * 10) + 1;
          const num2 = Math.floor(Math.random() * 10) + 1;
          setMathQuestion({ num1, num2, answer: num1 + num2 });
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-packages');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToAISearchFAQ = () => {
    const faqSection = document.getElementById('faq-9');
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add highlight animation
      faqSection.classList.add('animate-pulse');
      setTimeout(() => {
        faqSection.classList.remove('animate-pulse');
      }, 2000);
    }
  };

  const packages = [
    {
      name: 'Starter Website',
      price: 399,
      features: [
        '1-3 pages',
        'Mobile friendly design',
        'Contact form included',
        'Basic SEO optimization',
        'Social media links',
        'Fast loading speed',
        '30 days support'
      ],
      optional: 'Optional gallery: +$100 for 20 images'
    },
    {
      name: 'Business Website',
      price: 599,
      features: [
        '3-5 pages',
        'Everything in Starter, plus:',
        'Professional custom design',
        'Advanced SEO optimization',
        'Google Maps integration',
        'Contact form with validation',
        'Social media integration',
        '60 days support'
      ],
      optional: 'Optional gallery: +$100 for 20 images',
      popular: true
    },
    {
      name: 'Premium Website',
      price: 999,
      features: [
        '5-10 pages',
        'Everything in Business, plus:',
        'Advanced custom layout',
        'Priority support',
        'Social sharing features',
        'Gallery included (20 images)',
        'Blog page with 3 posts',
        '90 days support'
      ]
    }
  ];

  const addons = [
    {
      icon: 'ri-rocket-line',
      title: 'One-Time Website Launch & Setup',
      price: '+$150',
      unit: '(One time)',
      description: 'We handle all the technical work required to make your website live: connecting your domain, setting up your hosting, uploading the website, and ensuring everything works correctly. Perfect for clients who want a one-time setup without monthly hosting or support.'
    },
    {
      icon: 'ri-robot-line',
      title: 'AI Chat Integration',
      price: '+$100',
      unit: '(One time)',
      description: 'Smart AI-powered chatbot that answers customer questions 24/7, provides instant support, and helps convert visitors into customers. Fully customizable to your business needs.'
    },
    {
      icon: 'ri-image-line',
      title: 'Product Gallery',
      price: '+$100',
      unit: 'per 20 images',
      description: 'Professional image gallery with optimized layouts. Great for products, services, or portfolio'
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'E-Commerce Store',
      price: '+$799',
      unit: '10 products included',
      description: 'Complete online store with checkout, product pages, and payment integration. Includes setup for your first 30 products'
    },
    {
      icon: 'ri-add-circle-line',
      title: 'Additional Products',
      price: '+$100',
      unit: 'per 10 products',
      description: 'Add 15 extra products to your online store. Includes product titles, descriptions, pricing, and variant setup'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Client Dashboard',
      price: '+$500',
      unit: '(One time)',
      description: 'A secure self-service portal that lets you add, edit, or remove products anytime — without needing to contact us. Includes management for up to 100 products. If you need to add more, additional product packs are available.'
    },
    {
      icon: 'ri-article-line',
      title: 'Blog Post Writing',
      price: '+$50',
      unit: 'per blog post',
      description: 'Professional, SEO-optimized blog content written specifically for your business. Each post is 800-1200 words, includes relevant keywords, engaging headlines, and helps establish your expertise while improving search rankings.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Booking/Appointment System',
      price: '+$500',
      unit: 'with Telegram alerts',
      description: 'Complete online booking system with calendar integration, automated reminders, and instant Telegram notifications for new appointments. Perfect for service-based businesses.'
    }
  ];

  const maintenancePlans = [
    {
      name: 'Exposure',
      price: 39,
      features: [
        'Subdomain hosting included',
        'Security updates',
        'Daily backups',
        'Email support',
        '99.9% uptime guarantee'
      ]
    },
    {
      name: 'Starter',
      price: 99,
      features: [
        'Everything in Exposure',
        '✨ Free Custom Domain',
        '✨ SSL certificate',
        '✨ Domain name management',
        '✨ 1 professional domain email (info@yourdomain.com) set up and connected to your Gmail',
        '3 hours of updates per month',
        'Content changes',
        'Content Updates & Basic SEO',
        'Priority email support'
      ],
      popular: true
    },
    {
      name: 'Standard',
      price: 199,
      features: [
        'Everything in Starter',
        'Free Custom Domain',
        '1 hour of updates per month',
        '✨ SSL certificate',
        '✨ 1 professional domain email (info@yourdomain.com) set up and connected to your Gmail',
        'Content Updates & Basic SEO',
        'Security & Stability',
        'Emergency Fixes',
        'Priority Support'
      ],
      popular: true
    },
    {
      name: 'Premium',
      price: 299,
      features: [
        'Everything in Standard',
        'Free Custom Domain',
        '3 hours of updates per month',
        '• Add New Section',
        '• Add New Page',
        '• Add New Images',
        '1 Blog Post/Month',
        'Advanced Analytics',
        '24/7 Priority Support'
      ],
      popular: true
    },
    {
      name: 'AI Search Optimization Plan',
      price: 499,
      features: [
        'Everything in Premium',
        'Free Custom Domain',
        '2 Professional email setup',
        'Keyword research & optimization',
        'Local & international SEO',
        'Schema markup implementation',
        'AI search (ChatGPT, Gemini +)',
        'AI chat integration included',
        'Analytics & tracking setup',
        'Telegram integration for contact forms',
        'Monthly SEO report',
        'Ranking improvement tracking',
        'Google Business optimization',
        'Ongoing content optimization'
      ],
      highlight: true
    }
  ];

  const comparisonFeatures = [
    { name: 'Pages', values: ['1-3', '3-5', '5-10'] },
    { name: 'Design', values: ['Mobile friendly', 'Professional custom', 'Advanced custom'] },
    { name: 'SEO', values: ['Basic', 'Advanced', 'Advanced'] },
    { name: 'Support', values: ['30 days', '60 days', '90 days'] },
    { name: 'Gallery', values: ['Optional +$100', 'Optional +$100', 'Included (20 images)'] },
    { name: 'Blog', values: ['No', 'No', '3 posts included'] },
    { name: 'Social Sharing', values: ['No', 'No', 'Yes'] },
    { name: 'Google Maps', values: ['No', 'Yes', 'Yes'] },
    { name: 'Mobile Responsive', values: ['Yes', 'Yes', 'Yes'] },
    { name: 'Contact Form', values: ['Basic', 'With validation', 'With validation'] },
    { name: 'Social Links', values: ['Yes', 'Yes', 'Yes'] },
    { name: 'Fast Loading', values: ['Yes', 'Yes', 'Yes'] }
  ];

  const faqData = [
    {
      question: "What's included in the website design packages?",
      answer: "Each package includes a fully responsive, mobile-friendly website with professional design, contact forms, social media integration, and basic SEO optimization. Higher-tier packages include additional pages, advanced features, and extended support periods.",
      id: "faq-1"
    },
    {
      question: "Do I need to pay for hosting separately?",
      answer: "The Basic plan ($39/month) includes subdomain hosting. For a custom domain with your business name, choose the Starter Plus ($99/month) or higher plans, which include free domain registration, hosting, and SSL certificate.",
      id: "faq-2"
    },
    {
      question: "Can I switch between monthly support plans?",
      answer: "Yes! You can upgrade or downgrade your monthly support plan at any time. Changes will take effect at the start of your next billing cycle. We'll help you transition smoothly between plans.",
      id: "faq-3"
    },
    {
      question: "What happens if I cancel my monthly support plan?",
      answer: "You can cancel anytime with 30 days notice. Your website will remain live until the end of your billing period. We can also provide you with a backup of your website files if you want to host it elsewhere.",
      id: "faq-4"
    },
    {
      question: "How does the free homepage preview work?",
      answer: "Simply fill out the preview request form with your business details. We'll create a custom homepage design for you within 4 -7 days and host it on a private subdomain. You can review it online, and only pay if you're completely satisfied with the design.",
      id: "faq-5"
    },
    {
      question: "Can I add e-commerce to my website later?",
      answer: "Absolutely! You can add the E-Commerce Store add-on ($799) to any package at any time. This includes a complete online store with product pages, shopping cart, checkout, and payment integration for up to 10 products.",
      id: "faq-6"
    },
    {
      question: "What's the difference between one-time setup and monthly hosting?",
      answer: "The One-Time Website Launch ($150) is perfect if you have your own domain & hosting and just need us to set everything up once. Monthly hosting plans include subdomain/domain, ongoing support, updates, backups, security, and technical maintenance to keep your site running smoothly.",
      id: "faq-7"
    },
    {
      question: "Is the 20% yearly discount available for all plans?",
      answer: "Yes! The 20% discount applies to all monthly hosting & support plans when you pay annually. For example, the Standard plan at $199/month becomes just $159/month when paid yearly, saving you $478 per year.",
      id: "faq-8"
    },
    {
      question: "What is AI Search Optimization and why does it cost $499/month?",
      answer: "<strong className=\"text-purple-600\">AI Search Optimization (ASO)</strong> is the next evolution of SEO, moving beyond simply ranking links to positioning your business as the definitive source. We actively structure and refine your content to be instantly recognized by modern AI tools, including Google's powerful AI Overviews and chat features. This process ensures that when users ask natural language questions, your content is chosen to provide the direct answer, granting you the most valuable search real estate available today. By achieving this level of semantic authority, your business gains maximum visibility and secures a robust, future-proof path for sustainable growth. This premium service includes continuous content optimization, AI-specific schema markup, monthly performance reports, and dedicated account management to keep you ahead of the competition.",
      id: "faq-9"
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Website Design Pricing & Packages",
    "description": "Affordable website design packages for businesses in Vancouver, BC, and Canada. Choose from Starter, Business, or Premium packages with flexible monthly support plans.",
    "url": "https://businesswebsitepro.com/pricing",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://businesswebsitepro.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Pricing",
          "item": "https://businesswebsitepro.com/pricing"
        }
      ]
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Starter Website Package",
        "description": "Perfect for small businesses and startups. Includes 1-3 pages, mobile-responsive design, contact form, and basic SEO optimization.",
        "price": "399",
        "priceCurrency": "CAD",
        "category": "Website Design",
        "itemOffered": {
          "@type": "Service",
          "name": "Starter Website Design",
          "provider": {
            "@type": "LocalBusiness",
            "name": "BusinessWebsitePro"
          }
        }
      },
      {
        "@type": "Offer",
        "name": "Business Website Package",
        "description": "Most popular choice for growing businesses. Includes 3-5 pages, professional custom design, advanced SEO, and Google Maps integration.",
        "price": "599",
        "priceCurrency": "CAD",
        "category": "Website Design",
        "itemOffered": {
          "@type": "Service",
          "name": "Business Website Design",
          "provider": {
            "@type": "LocalBusiness",
            "name": "BusinessWebsitePro"
          }
        }
      },
      {
        "@type": "Offer",
        "name": "Premium Website Package",
        "description": "Complete solution for established businesses. Includes 5-10 pages, advanced custom layout, product gallery, blog section, and priority support.",
        "price": "999",
        "priceCurrency": "CAD",
        "category": "Website Design",
        "itemOffered": {
          "@type": "Service",
          "name": "Premium Website Design",
          "provider": {
            "@type": "LocalBusiness",
            "name": "BusinessWebsitePro"
          }
        }
      }
    ]
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[75vh] max-h-[85vh] flex items-center justify-center overflow-hidden pb-12">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.readdy.ai/image/afbb870fba5009b6251518206686e578/2d22fa2dd98cdcab98ef4f1ec23cc841.png"
            alt="Professional web design pricing"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        {/* Navbar */}
        <div className="absolute top-0 left-0 right-0 z-30">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-3 cursor-pointer">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-window-line text-2xl text-white"></i>
                </div>
                <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  BusinessWebsitePro
                </span>
              </a>
              
              <nav className="hidden md:flex items-center gap-8">
                <a href="/" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Home</a>
                <a href="/about" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">About</a>
                <a href="/services" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Services</a>
                <a href="/portfolio" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Portfolio</a>
                <a href="/pricing" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Pricing</a>
                <a href="/blog" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Blog</a>
                <a href="/contact" className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap">
                  Contact Us
                </a>
              </nav>
            </div>
          </div>
        </div>

        {/* No Upfront Payment Badge */}
        <div className="absolute top-48 right-8 z-20 animate-pulse">
          <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-6 py-3 rounded-2xl shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-300">
            <div className="flex items-center gap-2">
              <i className="ri-gift-line text-2xl"></i>
              <div>
                <div className="font-bold text-lg whitespace-nowrap">No Upfront Payment</div>
                <div className="text-xs opacity-90">Pay After Approval</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          {/* Glass Card - Left aligned with marginLeft: 8rem */}
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-3xl p-8 sm:p-12 shadow-2xl" style={{ marginLeft: '8rem', maxWidth: '600px' }}>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow-lg">
              No hidden fees. No surprises. Just beautiful, functional websites with guaranteed full ownership.
            </p>
            
            {/* CTA Buttons - Bottom right with extra spacing (2 inches = ~150px down), moved 1 inch (~75px) left */}
            <div className="flex flex-wrap gap-4 justify-end" style={{ marginTop: '150px', marginRight: '75px' }}>
              <a
                href="/booking"
                className="group relative px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xl"></i>
                Book Free Consultation
              </a>
              <a
                href="/portfolio"
                className="group relative px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line text-xl"></i>
                View Our Work
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Try Before You Pay Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-10 relative overflow-hidden border border-gray-100">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-100 to-blue-100 rounded-full opacity-30 -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full opacity-30 -ml-24 -mb-24"></div>

            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#14B8A6] to-[#0d9488] text-white px-5 py-2 rounded-full font-semibold text-sm mb-4 shadow-lg">
                  <i className="ri-star-line text-lg"></i>
                  100% RISK-FREE
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  Try Before You Pay — Free Homepage Preview
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  Start risk-free. We'll design a custom homepage preview using your business name, brand colors, and services — with <strong className="text-[#14B8A6]">no upfront payment required</strong>.
                </p>
              </div>

              {/* Key Benefits Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <div className="flex items-start gap-3 bg-gradient-to-br from-[#F0FDF4] to-white p-4 rounded-xl border border-[#14B8A6]/20 shadow-sm">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#14B8A6] to-[#0d9488] rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className="ri-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm">Custom Homepage Preview</h3>
                    <p className="text-xs text-gray-600">Created specifically for your business</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-gradient-to-br from-[#FEF3C7] to-white p-4 rounded-xl border border-[#C4F547]/30 shadow-sm">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#C4F547] to-[#b8d93d] rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className="ri-shield-check-line text-gray-900 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm">No Upfront Payment</h3>
                    <p className="text-xs text-gray-600">Zero risk, zero commitment</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-gradient-to-br from-[#E0F2FE] to-white p-4 rounded-xl border border-blue-200 shadow-sm">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className="ri-global-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm">Private Subdomain</h3>
                    <p className="text-xs text-gray-600">View your design online securely</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-gradient-to-br from-[#FCE7F3] to-white p-4 rounded-xl border border-pink-200 shadow-sm">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className="ri-eye-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm">Approve First</h3>
                    <p className="text-xs text-gray-600">Only pay $100 deposit after approval</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-gradient-to-br from-[#F3E8FF] to-white p-4 rounded-xl border border-purple-200 shadow-sm">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className="ri-shopping-bag-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm">Choose Any Package</h3>
                    <p className="text-xs text-gray-600">Select after you're satisfied</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-gradient-to-br from-[#FEF3C7] to-white p-4 rounded-xl border border-orange-200 shadow-sm">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className="ri-star-smile-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm">100% Risk-Free</h3>
                    <p className="text-xs text-gray-600">Complete peace of mind</p>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="text-center mt-6">
                <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                  <i className="ri-shield-check-line text-[#14B8A6]"></i>
                  No credit card required • No obligations • 100% free preview
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Form Modal */}
      {showPreviewForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Close Button */}
              <button
                onClick={() => setShowPreviewForm(false)}
                className="cursor-pointer float-right w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>

              {/* Form Header */}
              <div className="mb-8">
                <div className="inline-flex items-center gap-2 bg-[#14B8A6]/10 text-[#14B8A6] px-4 py-2 rounded-full font-semibold text-sm mb-4">
                  <i className="ri-gift-line"></i>
                  FREE PREVIEW REQUEST
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Get Your Free Homepage Preview</h3>
                <p className="text-gray-600">Fill out the form below and we'll create a custom homepage preview for your business — completely free!</p>
              </div>

              {formSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-green-500"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Request Submitted!</h4>
                  <p className="text-gray-600">We'll start working on your free homepage preview and contact you within 24 hours.</p>
                </div>
              ) : (
                <form id="preview-form" data-readdy-form onSubmit={handleFormSubmit} className="space-y-6">
                  {/* Honeypot field - hidden from users, only bots will fill it */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={(e) => setFormData({...formData, honeypot: e.target.value})}
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Business Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                      placeholder="Your Business Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      required
                      className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base ${
                        formErrors.email ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="john@example.com"
                    />
                    {formErrors.email && (
                      <p className="text-xs text-red-600 mt-1 flex items-center">
                        <i className="ri-error-warning-line mr-1"></i>
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Type of Service/Business <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={(e) => setFormData({...formData, serviceType: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                    >
                      <option value="">Select your business type</option>
                      <option value="Restaurant">Restaurant</option>
                      <option value="Retail Store">Retail Store</option>
                      <option value="Professional Services">Professional Services</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Construction">Construction</option>
                      <option value="Beauty & Wellness">Beauty & Wellness</option>
                      <option value="Education">Education</option>
                      <option value="E-commerce">E-commerce</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Math Verification */}
                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6">
                    <label htmlFor="mathAnswer" className="block text-sm font-semibold text-gray-900 mb-3">
                      <i className="ri-shield-check-line text-yellow-600 mr-2"></i>
                      Security Check: What is {mathQuestion.num1} + {mathQuestion.num2}? *
                    </label>
                    <input
                      type="number"
                      id="mathAnswer"
                      name="mathAnswer"
                      value={formData.mathAnswer}
                      onChange={(e) => setFormData({...formData, mathAnswer: e.target.value})}
                      required
                      className="w-full md:w-32 px-4 py-3 border-2 border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-base font-semibold"
                      placeholder="Answer"
                    />
                    <p className="text-xs text-gray-600 mt-2">
                      This helps us prevent spam and ensure you're a real person
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="cursor-pointer w-full bg-gradient-to-r from-[#14B8A6] to-[#0d9488] text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap"
                  >
                    <i className="ri-send-plane-fill mr-2"></i>
                    Submit Request
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting this form, you agree to be contacted about your free homepage preview.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Website Design Packages */}
      <section className="py-20 bg-white" id="packages">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                id={`${pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`bg-white rounded-3xl p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl flex flex-col ${
                  pkg.popular ? 'border-4 border-[#0d3b66] shadow-xl' : 'border-2 border-blue-200'
                }`}
              >
                <div className="h-8 mb-4">
                  {pkg.popular && (
                    <span className="inline-block bg-[#C4F547] text-gray-900 text-xs font-bold px-4 py-1.5 rounded-full">
                      MOST POPULAR
                    </span>
                  )}
                </div>
                
                <h3 className="text-3xl font-bold text-[#0d3b66] mb-4">{pkg.name}</h3>
                
                <div className="mb-6">
                  <p className="text-5xl font-bold text-gray-900">${pkg.price}</p>
                </div>
                
                <div className="border-t border-gray-200 pt-6 mb-6 flex-grow">
                  <ul className="space-y-4">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-base text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-auto">
                  <Link to={`/booking?package=${encodeURIComponent(pkg.name)}&price=${pkg.price}`} className="cursor-pointer">
                    <button className="w-full bg-[#0d3b66] text-white py-4 rounded-xl font-semibold hover:bg-[#0a2d4d] transition-all duration-300 shadow-md hover:shadow-xl cursor-pointer whitespace-nowrap">
                      Get Started
                    </button>
                  </Link>
                  
                  {pkg.optional && (
                    <p className="text-sm text-gray-500 italic mt-4 text-center">{pkg.optional}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-2 rounded-full mb-4">
              COMMON FEATURES
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Compare All Features</h2>
            <p className="text-lg text-gray-600">See what's included in each package</p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0d3b66] text-white sticky top-0">
                  <tr>
                    <th className="px-6 py-5 text-left text-lg font-bold">Features</th>
                    {packages.map((pkg, index) => (
                      <th key={index} className="px-6 py-5 text-center">
                        <div className="text-xl font-bold mb-2">{pkg.name}</div>
                        <div className="text-2xl font-bold">
                          ${pkg.price}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 font-semibold text-gray-900">{feature.name}</td>
                      {feature.values.map((value, pkgIdx) => (
                        <td key={pkgIdx} className="px-6 py-4 text-center">
                          {value === 'Yes' ? (
                            <i className="ri-check-line text-2xl text-green-500"></i>
                          ) : value === 'No' ? (
                            <i className="ri-close-line text-2xl text-gray-300"></i>
                          ) : (
                            <span className="text-gray-700">{value}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Add-Ons Button */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => {
                const maintenanceSection = document.getElementById('maintenance');
                if (maintenanceSection) {
                  maintenanceSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="cursor-pointer inline-flex items-center gap-3 bg-gradient-to-b from-[#0d3b66] to-[#0a2d4d] text-white px-10 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap"
            >
              <i className="ri-arrow-down-circle-line text-2xl"></i>
              Monthly Maintenance Plan
            </button>
            
            <button
              onClick={() => {
                const addonsSection = document.getElementById('add-ons');
                if (addonsSection) {
                  addonsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="cursor-pointer inline-flex items-center gap-3 bg-gradient-to-b from-[#0d3b66] to-[#0a2d4d] text-white px-10 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap"
            >
              <i className="ri-arrow-down-circle-line text-2xl"></i>
              Click here to view Add-Ons
            </button>
          </div>
        </div>
      </section>

      {/* Yearly Discount Banner */}
      <section className="py-16 bg-gradient-to-r from-[#E8F5E9] via-[#F1F8E9] to-[#E8F5E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 relative overflow-hidden border border-gray-200">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#0d3b66]/5 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#14B8A6]/5 rounded-full -ml-24 -mb-24"></div>
            
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center gap-2 bg-[#C4F547] text-gray-900 px-6 py-2 rounded-full font-bold text-sm mb-6">
                <i className="ri-gift-line text-xl"></i>
                SPECIAL OFFER
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Save 20% with Yearly Payment
              </h2>
              
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Choose any monthly hosting & support plan and pay yearly to get <span className="font-bold text-[#0d3b66]">20% off</span> the total cost. That's 2 months free!
              </p>
              
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-8">
                <div className="bg-gray-50 rounded-2xl p-6 flex-1 max-w-xs">
                  <p className="text-sm text-gray-500 mb-2">Monthly Payment</p>
                  <p className="text-3xl font-bold text-gray-900">$199<span className="text-lg text-gray-500">/month</span></p>
                  <p className="text-sm text-gray-500 mt-2">= $2,388/year</p>
                </div>
                
                <div className="flex items-center justify-center">
                  <i className="ri-arrow-right-line text-3xl text-[#0d3b66] hidden md:block"></i>
                  <i className="ri-arrow-down-line text-3xl text-[#0d3b66] md:hidden"></i>
                </div>
                
                <div className="bg-gradient-to-br from-[#14B8A6] to-[#0d9488] rounded-2xl p-6 flex-1 max-w-xs relative">
                  <div className="absolute -top-3 -right-3 bg-[#C4F547] text-gray-900 text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                    SAVE $478
                  </div>
                  <p className="text-sm text-white/90 mb-2">Yearly Payment</p>
                  <p className="text-3xl font-bold text-white">$159<span className="text-lg text-white/90">/month</span></p>
                  <p className="text-sm text-white/90 mt-2">= $1,910/year</p>
                </div>
              </div>
              
              <Link to="/booking?plan=yearly" className="cursor-pointer">
                <button className="inline-flex items-center gap-3 bg-[#0d3b66] text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-[#0a2d4d] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 whitespace-nowrap">
                  <i className="ri-calendar-check-line text-xl"></i>
                  Switch to Yearly & Save
                </button>
              </Link>
              
              <p className="text-sm text-gray-500 mt-6">
                * Applies to all monthly hosting & support plans
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly Maintenance Plans */}
      <section className="py-20 bg-white" id="maintenance">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-2 rounded-full mb-4">
              ONGOING SUPPORT
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Monthly Hosting &amp; Support Plans
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Keep your website running smoothly with our comprehensive hosting and support packages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Dual Card - Exposure & Starter */}
            <div className="space-y-4">
              {/* Exposure Plan */}
              <div id="exposure-plan" className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 flex flex-col">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Exposure</h3>
                  <div className="flex items-baseline justify-center mb-4">
                    <span className="text-4xl font-bold text-gray-900">$39</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-6 flex-grow">
                  <li className="flex items-start text-sm">
                    <i className="ri-check-line text-teal-500 text-lg mr-2 flex-shrink-0 mt-0.5"></i>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-700">Subdomain hosting</span>
                      <div className="relative group">
                        <i className="ri-information-line text-teal-500 text-base cursor-pointer"></i>
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                          Your website will be hosted at yourbrand.businesswebsitepro.com
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start text-sm">
                    <i className="ri-check-line text-teal-500 text-lg mr-2 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Security updates</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <i className="ri-check-line text-teal-500 text-lg mr-2 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Daily backups</span>
                  </li>
                </ul>
              </div>

              {/* Starter Plan */}
              <div id="starter-plan" className="bg-gradient-to-br from-teal-50 to-white border-2 border-teal-300 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 relative flex flex-col">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-teal-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  BEST VALUE
                </div>
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Starter</h3>
                  <div className="flex items-baseline justify-center mb-4">
                    <span className="text-5xl font-bold text-teal-600">$99</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Everything in Starter</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-700 font-semibold">Free Custom Domain</span>
                      <div className="relative group">
                        <i className="ri-information-line text-teal-500 text-base cursor-pointer"></i>
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                          Custom Domain & SSL Security Included
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-700 font-semibold">Free Hosting</span>
                      <div className="relative group">
                        <i className="ri-information-line text-teal-500 text-base cursor-pointer"></i>
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                          Premium, High-Speed Hosting Included
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Security updates</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Daily backups</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Standard Plan */}
            <div id="standard-plan" className="bg-gradient-to-br from-teal-50 to-white border-2 border-teal-500 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 relative transform hover:scale-105 flex flex-col">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-teal-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                MOST POPULAR
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Standard</h3>
                <div className="flex items-baseline justify-center mb-4">
                  <span className="text-5xl font-bold text-teal-600">$199</span>
                  <span className="text-gray-600 ml-2">/month</span>
                </div>
                <p className="text-sm text-gray-600">Perfect for growing businesses</p>
              </div>
              <ul className="space-y-4 mb-8 flex-grow">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Everything in Starter</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-700 font-semibold">Free Custom Domain</span>
                    <div className="relative group">
                      <i className="ri-information-line text-teal-500 text-base cursor-pointer"></i>
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                        Custom Domain & SSL Security Included
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-700 font-semibold">Free Hosting</span>
                    <div className="relative group">
                      <i className="ri-information-line text-teal-500 text-base cursor-pointer"></i>
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                        Premium, High-Speed Hosting Included
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">SSL Certificate</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">1 Professional Email</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">SEO Optimization</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">1 Hour Updates/Month</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">• Content Updates & Basic SEO</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Security & Stability</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Emergency Fixes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Priority Support</span>
                </li>
              </ul>
              <a
                href="/booking"
                className="block w-full bg-teal-500 text-white text-center py-4 rounded-xl font-semibold hover:bg-teal-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Get Started
              </a>
            </div>

            {/* Premium Plan */}
            <div id="premium-plan" className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Premium</h3>
                <div className="flex items-baseline justify-center mb-4">
                  <span className="text-5xl font-bold text-gray-900">$299</span>
                  <span className="text-gray-600 ml-2">/month</span>
                </div>
                <p className="text-sm text-gray-600">For established businesses</p>
              </div>
              <ul className="space-y-4 mb-8 flex-grow">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Everything in Standard</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-700 font-semibold">Free Custom Domain</span>
                    <div className="relative group">
                      <i className="ri-information-line text-teal-500 text-base cursor-pointer"></i>
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                        Custom Domain & SSL Security Included
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-700 font-semibold">Free Hosting</span>
                    <div className="relative group">
                      <i className="ri-information-line text-teal-500 text-base cursor-pointer"></i>
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                        Premium, High-Speed Hosting Included
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">SSL Certificate</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">1 Professional Email</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">SEO Optimization</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">3 hours of updates per month</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">• Add New Section</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">• Add New Page</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">• Add New Images</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">1 Blog Post/Month</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Advanced Analytics</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">24/7 Priority Support</span>
                </li>
              </ul>
              <a
                href="/booking"
                className="block w-full bg-gray-900 text-white text-center py-4 rounded-xl font-semibold hover:bg-gray-800 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Get Started
              </a>
            </div>

            {/* AI Search Optimization Plan */}
            <div id="ai-search-optimization" className="relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-10 shadow-2xl border-4 border-purple-400 transform hover:scale-105 transition-all duration-300">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                  🚀 FUTURE-PROOF YOUR BUSINESS
                </span>
              </div>

              <div className="text-center mb-8 mt-4">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">AI Search Optimization</h3>
                <div className="flex items-baseline justify-center gap-2 mb-4">
                  <span className="text-5xl font-bold text-purple-600 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">$499</span>
                  <span className="text-gray-600">/month</span>
                </div>
                <p className="text-base text-gray-600">Be the direct answer AI tools choose</p>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  'Everything in Premium',
                  '2 Professional email setup',
                  'AI Overview optimization (Google SGE)',
                  'Semantic authority building',
                  'Direct answer positioning',
                  'Voice search optimization',
                  'AI-specific schema markup',
                  'Continuous content refinement',
                  'Monthly AI visibility reports',
                  'Dedicated account manager'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-sm text-white"></i>
                    </div>
                    <span className="text-base text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* AI Search Optimization Button */}
              <div className="text-center mt-8 mb-6">
                <button
                  onClick={() => scrollToAISearchFAQ()}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-extrabold py-3 px-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap text-xs cursor-pointer"
                >
                  What is AI Search Optimization ?
                </button>
              </div>

              <Link to="/booking">
                <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap">
                  Get Started
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Add-Ons Pricing */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white" id="add-ons">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Add-On Services</h2>
            <p className="text-lg text-gray-600">Enhance your website with these optional add-ons</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {addons.map((addon, index) => {
              const addonId = addon.title.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-');
              return (
                <div 
                  key={index}
                  id={addonId}
                  className="bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 flex flex-col"
                >
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <i className={`${addon.icon} text-3xl text-[#0d3b66]`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{addon.title}</h3>
                  <div className="mb-3">
                    <span className="text-3xl font-bold text-[#0d3b66]">{addon.price}</span>
                    <span className="text-base text-gray-500 ml-2">{addon.unit}</span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-grow">{addon.description}</p>
                  <Link to={`/booking?addon=${encodeURIComponent(addon.title)}&price=${encodeURIComponent(addon.price)}`} className="cursor-pointer">
                    <button className="w-full bg-white text-[#0d3b66] border-2 border-[#0d3b66] py-3 rounded-lg font-semibold hover:bg-[#0d3b66] hover:text-white transition-all duration-300 whitespace-nowrap">
                      Add to Cart
                    </button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions - Combined Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our pricing and services
            </p>
          </div>

          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div
                key={index}
                id={faq.id}
                className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-[#0d3b66] transition-all duration-300 shadow-sm hover:shadow-lg"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-start justify-between gap-4 text-left"
                >
                  <h3 className="text-xl font-bold text-gray-900 flex-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${openFAQ === index ? 'up' : 'down'}-s-line text-2xl text-[#0d3b66] flex-shrink-0`}></i>
                </button>
                {openFAQ === index && (
                  <div className="mt-4 text-gray-700 leading-relaxed space-y-3">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}